import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Download, TrendingUp, ShoppingCart, Package, Calendar, Users, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const ReportsPage = () => {
  const [salesSummary, setSalesSummary] = useState({ daily: {}, weekly: {} });
  const [bestSellers, setBestSellers] = useState([]);
  const [salesByCategory, setSalesByCategory] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [operatorReport, setOperatorReport] = useState(null);
  const [operatorDetail, setOperatorDetail] = useState(null);
  const [showOperatorDetail, setShowOperatorDetail] = useState(false);
  const [operatorDays, setOperatorDays] = useState('30');
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState('7');

  useEffect(() => {
    fetchReportData();
  }, []);

  useEffect(() => {
    fetchTrendData();
  }, [trendDays]);

  useEffect(() => {
    fetchOperatorData();
  }, [operatorDays]);

  const fetchReportData = async () => {
    try {
      const [summaryRes, bestRes, categoryRes] = await Promise.all([
        axios.get(`${API}/reports/sales-summary`),
        axios.get(`${API}/reports/best-sellers?limit=10`),
        axios.get(`${API}/reports/sales-by-category`)
      ]);
      
      setSalesSummary(summaryRes.data);
      setBestSellers(bestRes.data);
      setSalesByCategory(categoryRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendData = async () => {
    try {
      const response = await axios.get(`${API}/reports/daily-trend?days=${trendDays}`);
      setDailyTrend(response.data.map(d => ({
        ...d,
        date: new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
      })));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const exportSales = () => {
    window.open(`${API}/reports/export/sales`, '_blank');
  };

  const exportStock = () => {
    window.open(`${API}/reports/export/stock`, '_blank');
  };

  const exportOperators = () => {
    window.open(`${API}/reports/export/by-operator?days=${operatorDays}`, '_blank');
  };

  const fetchOperatorData = async () => {
    try {
      const response = await axios.get(`${API}/reports/by-operator?days=${operatorDays}`);
      setOperatorReport(response.data);
    } catch (error) {
      console.error('Error fetching operator report:', error);
    }
  };

  const viewOperatorDetail = async (operatorId) => {
    try {
      const response = await axios.get(`${API}/reports/operator/${operatorId}?days=${operatorDays}`);
      setOperatorDetail(response.data);
      setShowOperatorDetail(true);
    } catch (error) {
      console.error('Error fetching operator detail:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading reports...</div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500 mt-1 font-body">Sales analytics and insights</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="export-sales-btn" onClick={exportSales}>
            <Download className="w-4 h-4 mr-2" />
            Export Sales
          </Button>
          <Button variant="outline" data-testid="export-stock-btn" onClick={exportStock}>
            <Download className="w-4 h-4 mr-2" />
            Export Stock
          </Button>
          <Button variant="outline" data-testid="export-operators-btn" onClick={exportOperators}>
            <Download className="w-4 h-4 mr-2" />
            Export Operators
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stats-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Today's Revenue</CardTitle>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="price-display text-3xl text-slate-900">
              £{(salesSummary.daily?.revenue || 0).toFixed(2)}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {salesSummary.daily?.transactions || 0} sales
            </p>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Weekly Revenue</CardTitle>
            <Calendar className="w-5 h-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="price-display text-3xl text-slate-900">
              £{(salesSummary.weekly?.revenue || 0).toFixed(2)}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {salesSummary.weekly?.transactions || 0} sales
            </p>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Avg. Transaction</CardTitle>
            <ShoppingCart className="w-5 h-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="price-display text-3xl text-slate-900">
              £{salesSummary.weekly?.transactions > 0 
                ? (salesSummary.weekly.revenue / salesSummary.weekly.transactions).toFixed(2)
                : '0.00'}
            </div>
            <p className="text-sm text-slate-500 mt-1">this week</p>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Top Product</CardTitle>
            <Package className="w-5 h-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="font-heading text-xl font-bold text-slate-900 truncate">
              {bestSellers[0]?.product_name || 'N/A'}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {bestSellers[0]?.total_quantity || 0} units
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="trend">
        <TabsList>
          <TabsTrigger value="trend" data-testid="tab-trend">Sales Trend</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Best Sellers</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">By Category</TabsTrigger>
          <TabsTrigger value="operators" data-testid="tab-operators">By Operator</TabsTrigger>
        </TabsList>

        <TabsContent value="trend" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Daily Sales Trend</CardTitle>
              <Select value={trendDays} onValueChange={setTrendDays}>
                <SelectTrigger className="w-32" data-testid="trend-days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {dailyTrend.length === 0 || dailyTrend.every(d => d.revenue === 0) ? (
                <div className="h-80 flex items-center justify-center text-slate-400">
                  No sales data for this period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={dailyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#71717A" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#71717A" tickFormatter={(v) => `£${v}`} />
                    <Tooltip 
                      formatter={(value) => [`£${value.toFixed(2)}`, 'Revenue']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E4E4E7' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#2563EB" 
                      strokeWidth={2}
                      dot={{ fill: '#2563EB', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Best Selling Products</CardTitle>
            </CardHeader>
            <CardContent>
              {bestSellers.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-slate-400">
                  No sales data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={bestSellers} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#71717A" />
                    <YAxis 
                      dataKey="product_name" 
                      type="category" 
                      width={120}
                      tick={{ fontSize: 11 }} 
                      stroke="#71717A"
                    />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'total_quantity' ? `${value} units` : `£${value.toFixed(2)}`,
                        name === 'total_quantity' ? 'Quantity' : 'Revenue'
                      ]}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E4E4E7' }}
                    />
                    <Bar dataKey="total_quantity" fill="#2563EB" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Revenue by Category</CardTitle>
              </CardHeader>
              <CardContent>
                {salesByCategory.length === 0 ? (
                  <div className="h-80 flex items-center justify-center text-slate-400">
                    No sales data yet
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={salesByCategory}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="total_revenue"
                        nameKey="category_name"
                        label={({ category_name, percent }) => 
                          `${category_name} ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={false}
                      >
                        {salesByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`£${value.toFixed(2)}`, 'Revenue']}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #E4E4E7' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Category Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {salesByCategory.length === 0 ? (
                  <div className="py-8 text-center text-slate-400">No sales data yet</div>
                ) : (
                  <div className="space-y-3">
                    {salesByCategory.map((cat, idx) => (
                      <div
                        key={cat.category_id || idx}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                          />
                          <span className="font-medium text-slate-700">{cat.category_name}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-heading font-bold text-slate-900">
                            £{cat.total_revenue.toFixed(2)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {cat.total_quantity} items
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="operators" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Operator Performance</CardTitle>
              <Select value={operatorDays} onValueChange={setOperatorDays}>
                <SelectTrigger className="w-36" data-testid="operator-days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {!operatorReport || operatorReport.operators.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  No operator sales data for this period
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Operator bar chart */}
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={operatorReport.operators}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                      <XAxis dataKey="operator_name" tick={{ fontSize: 12 }} stroke="#71717A" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#71717A" tickFormatter={(v) => `£${v}`} />
                      <Tooltip
                        formatter={(value, name) => [
                          name === 'total_revenue' ? `£${value.toFixed(2)}` : value,
                          name === 'total_revenue' ? 'Revenue' : 'Sales'
                        ]}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #E4E4E7' }}
                      />
                      <Bar dataKey="total_revenue" fill="#2563EB" radius={[4, 4, 0, 0]} name="total_revenue" />
                      <Bar dataKey="total_sales" fill="#10B981" radius={[4, 4, 0, 0]} name="total_sales" />
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Operator table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="operator-report-table">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="text-left p-3 font-medium text-slate-600">Operator</th>
                          <th className="text-right p-3 font-medium text-slate-600">Sales</th>
                          <th className="text-right p-3 font-medium text-slate-600">Revenue</th>
                          <th className="text-right p-3 font-medium text-slate-600">VAT</th>
                          <th className="text-right p-3 font-medium text-slate-600">Items</th>
                          <th className="text-right p-3 font-medium text-slate-600">Avg</th>
                          <th className="text-center p-3 font-medium text-slate-600">Card</th>
                          <th className="text-center p-3 font-medium text-slate-600">Cash</th>
                          <th className="text-center p-3 font-medium text-slate-600">Account</th>
                          <th className="p-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {operatorReport.operators.map((op) => (
                          <tr key={op.operator_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                  <Users className="w-4 h-4 text-blue-600" />
                                </div>
                                <span className="font-medium text-slate-800">{op.operator_name}</span>
                              </div>
                            </td>
                            <td className="p-3 text-right font-mono text-slate-700">{op.total_sales}</td>
                            <td className="p-3 text-right font-mono font-semibold text-slate-900">£{op.total_revenue.toFixed(2)}</td>
                            <td className="p-3 text-right font-mono text-slate-500">£{op.total_vat.toFixed(2)}</td>
                            <td className="p-3 text-right font-mono text-slate-700">{op.total_items}</td>
                            <td className="p-3 text-right font-mono text-slate-700">£{op.avg_transaction.toFixed(2)}</td>
                            <td className="p-3 text-center">
                              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                {op.by_payment_method?.card || 0}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                                {op.by_payment_method?.cash || 0}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                                {op.by_payment_method?.account || 0}
                              </span>
                            </td>
                            <td className="p-3">
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`view-operator-${op.operator_id}`}
                                onClick={() => viewOperatorDetail(op.operator_id)}
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Operator Detail Dialog */}
      <Dialog open={showOperatorDetail} onOpenChange={setShowOperatorDetail}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {operatorDetail?.operator?.username || 'Operator'} — Performance Detail
            </DialogTitle>
            <DialogDescription>
              Sales performance breakdown for the last {operatorDays} days
            </DialogDescription>
          </DialogHeader>
          {operatorDetail && (
            <div className="space-y-6 mt-2">
              {/* Summary row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-500">Sales</p>
                  <p className="text-xl font-bold text-slate-900">{operatorDetail.summary.total_sales}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-500">Revenue</p>
                  <p className="text-xl font-bold text-slate-900">£{operatorDetail.summary.total_revenue.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-500">Items</p>
                  <p className="text-xl font-bold text-slate-900">{operatorDetail.summary.total_items}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-500">Avg Transaction</p>
                  <p className="text-xl font-bold text-slate-900">£{operatorDetail.summary.avg_transaction.toFixed(2)}</p>
                </div>
              </div>

              {/* Payment methods */}
              <div>
                <h3 className="text-sm font-medium text-slate-600 mb-2">Payment Methods</h3>
                <div className="flex gap-3">
                  {operatorDetail.by_payment_method.map((m) => (
                    <div key={m.method} className="flex-1 p-3 bg-slate-50 rounded-lg text-center">
                      <p className="text-xs text-slate-500 capitalize">{m.method}</p>
                      <p className="font-bold text-slate-900">£{m.revenue.toFixed(2)}</p>
                      <p className="text-xs text-slate-500">{m.count} sales</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top products */}
              {operatorDetail.top_products.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-600 mb-2">Top Products Sold</h3>
                  <div className="space-y-2">
                    {operatorDetail.top_products.map((p, idx) => (
                      <div key={p.product_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-400 w-5 text-right">#{idx + 1}</span>
                          <span className="text-sm font-medium text-slate-700">{p.product_name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-slate-900">£{p.total_revenue.toFixed(2)}</span>
                          <span className="text-xs text-slate-500 ml-2">({p.total_quantity} units)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Daily trend mini chart */}
              {operatorDetail.daily_breakdown.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-600 mb-2">Daily Breakdown</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={[...operatorDetail.daily_breakdown].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        stroke="#71717A"
                        tickFormatter={(v) => {
                          const d = new Date(v);
                          return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                        }}
                      />
                      <YAxis tick={{ fontSize: 10 }} stroke="#71717A" tickFormatter={(v) => `£${v}`} />
                      <Tooltip
                        formatter={(value, name) => [
                          name === 'revenue' ? `£${value.toFixed(2)}` : value,
                          name === 'revenue' ? 'Revenue' : name === 'transactions' ? 'Sales' : 'Items'
                        ]}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #E4E4E7' }}
                      />
                      <Bar dataKey="revenue" fill="#2563EB" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Refunds/Voids */}
              <div className="flex gap-4 text-sm">
                <div className="p-2 bg-red-50 rounded-lg text-center flex-1">
                  <p className="text-xs text-red-500">Refunds Processed</p>
                  <p className="font-bold text-red-700">{operatorDetail.refunds_processed}</p>
                </div>
                <div className="p-2 bg-orange-50 rounded-lg text-center flex-1">
                  <p className="text-xs text-orange-500">Voids Processed</p>
                  <p className="font-bold text-orange-700">{operatorDetail.voids_processed}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
